/**
 * useStaffSession.js — small hook every /staff/* page uses to validate
 * the PIN JWT + expose staff role/name. Redirects to /staff/login on
 * missing or expired token.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export const STAFF_TOKEN = "raro_staff_token";
export const STAFF_INFO  = "raro_staff_info";
export const STAFF_EXP   = "raro_staff_exp";

// ── Session-expiry 401 interceptor ───────────────────────────────────
// The PIN JWT has an N-hour TTL. useStaffSession only checks it on MOUNT,
// so a session that lapses WHILE a manager is working (e.g. mid-edit in
// the menu editor) used to surface a raw 401 "Token expired" error and
// leave them stuck. This global axios interceptor catches any 401 from a
// venue/staff API, clears the stale session, and bounces to the PIN pad
// (/staff/login) with ?next= so they land back where they were after
// re-entering their PIN. Registered ONCE; `_navRef` holds the latest
// react-router navigate so the redirect is an SPA transition.
let _staffRespInterceptor = null;
const _navRef = { current: null };
const _STAFF_API_RX = /\/(venue-terminal|venue-pin|venue-stock|raro)\b/;

function _clearStaffSession() {
  localStorage.removeItem(STAFF_TOKEN);
  localStorage.removeItem(STAFF_INFO);
  localStorage.removeItem(STAFF_EXP);
  delete axios.defaults.headers.common.Authorization;
}

function _installStaffAuthInterceptor() {
  if (_staffRespInterceptor !== null) return;
  _staffRespInterceptor = axios.interceptors.response.use(
    (r) => r,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      const hasToken = !!localStorage.getItem(STAFF_TOKEN);
      const isPinLogin = url.includes("/venue-pin/login");
      if (
        status === 401 &&
        hasToken &&
        !isPinLogin &&
        _STAFF_API_RX.test(url) &&
        typeof window !== "undefined" &&
        window.location.pathname !== "/staff/login"
      ) {
        _clearStaffSession();
        const here = window.location.pathname + window.location.search;
        const dest = `/staff/login?next=${encodeURIComponent(here)}&expired=1`;
        if (_navRef.current) _navRef.current(dest, { replace: true });
        else window.location.assign(dest);
      }
      return Promise.reject(error);
    }
  );
}

export function useStaffSession(returnTo = "/staff") {
  const nav = useNavigate();
  const [staff, setStaff] = useState(null);

  useEffect(() => {
    // Every /staff/* page MUST be invisible to search engines.  We
    // inject a hard noindex meta tag on mount and strip it on unmount
    // so returning to a public page (e.g. /raro/menu) restores normal
    // indexing.  Robots.txt also disallows /staff — this belt-and-
    // braces approach covers crawlers that ignore robots.txt.
    let robots = document.head.querySelector('meta[name="robots"]');
    const prevContent = robots?.getAttribute("content") || null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow, noarchive, nosnippet, noimageindex");

    const tok = localStorage.getItem(STAFF_TOKEN);
    const exp = Number(localStorage.getItem(STAFF_EXP) || 0);
    const raw = localStorage.getItem(STAFF_INFO);
    if (!tok || !exp || exp < Date.now()) {
      localStorage.removeItem(STAFF_TOKEN);
      localStorage.removeItem(STAFF_INFO);
      localStorage.removeItem(STAFF_EXP);
      nav(`/staff/login?next=${encodeURIComponent(returnTo)}`, { replace: true });
      return () => {};
    }
    try { setStaff(JSON.parse(raw)); } catch { setStaff(null); }
    axios.defaults.headers.common.Authorization = `Bearer ${tok}`;
    // Keep the interceptor's navigate fresh + ensure it's installed so a
    // mid-session PIN expiry re-prompts instead of erroring.
    _navRef.current = nav;
    _installStaffAuthInterceptor();

    return () => {
      // Restore whatever the next page (or default SEO block) intended.
      if (prevContent === null) {
        robots?.parentNode?.removeChild(robots);
      } else {
        robots?.setAttribute("content", prevContent);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    localStorage.removeItem(STAFF_TOKEN);
    localStorage.removeItem(STAFF_INFO);
    localStorage.removeItem(STAFF_EXP);
    delete axios.defaults.headers.common.Authorization;
    nav("/staff/login", { replace: true });
  };

  return { staff, signOut, isManager: staff?.role === "manager" };
}

export function staffAuthHeader() {
  const tok = localStorage.getItem(STAFF_TOKEN);
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}
